import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  Button,
  Divider,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Folder as ProjectIcon,
  Assignment as CaseIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as InvoiceIcon,
  Pending as PendingIcon,
  Comment as CommentIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { notificationService, Notification } from '../services/notificationService';
import { firmAPI, projectAPI, caseAPI } from '../services/api';
import { CircularProgress } from '@mui/material';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalFirms: 0,
    activeProjects: 0,
    openCases: 0,
    monthlyGrowth: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };

  useEffect(() => {
    if (!user) return;
    loadNotifications();
    if (isPmpAdminOrSiteAdmin()) {
      loadDashboardStats();
    }
  }, [user]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const [notificationsRes, countRes] = await Promise.all([
        notificationService.getNotifications(), // all types: case_comment, pdf_page_count, etc.
        notificationService.getUnreadCount(),
      ]);
      setNotifications(notificationsRes.data.data || []);
      setUnreadCount(countRes.data.count || 0);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.read_at) {
      try {
        await notificationService.markAsRead(notification.id);
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, read_at: new Date().toISOString() } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }
    
    // Navigate to the comment
    if (notification.action_url) {
      // Extract URL path from action_url (it might be a full URL)
      const url = new URL(notification.action_url, window.location.origin);
      navigate(url.pathname + url.hash);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const loadDashboardStats = async () => {
    try {
      setStatsLoading(true);
      
      // Fetch all stats in parallel
      const [firmsRes, projectsRes, allCasesRes] = await Promise.all([
        firmAPI.getAll({ per_page: 1 }), // Just get total count
        projectAPI.getAll({ status: 'active', per_page: 1 }),
        caseAPI.getAll({ per_page: 1000 }), // Get all cases to filter and calculate growth
      ]);

      // Get total firms count from pagination
      const totalFirms = firmsRes.data.data?.total || firmsRes.data.data?.data?.length || 0;
      
      // Get active projects count from pagination
      const activeProjects = projectsRes.data.data?.total || projectsRes.data.data?.data?.length || 0;

      // Get all cases from response
      const allCases = allCasesRes.data.data?.data || allCasesRes.data.data || [];
      
      // Filter open cases (exclude closed, completed, cancelled)
      const openCases = allCases.filter((c: any) => {
        const status = c.status?.toLowerCase() || '';
        return !['closed', 'completed', 'cancelled'].includes(status);
      }).length;

      // Calculate monthly growth (cases created this month vs last month)
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

      const thisMonthCases = allCases.filter((c: any) => {
        if (!c.created_at) return false;
        const created = new Date(c.created_at);
        return created >= thisMonthStart;
      }).length;

      const lastMonthCases = allCases.filter((c: any) => {
        if (!c.created_at) return false;
        const created = new Date(c.created_at);
        return created >= lastMonthStart && created <= lastMonthEnd;
      }).length;

      let monthlyGrowth = 0;
      if (lastMonthCases > 0) {
        monthlyGrowth = ((thisMonthCases - lastMonthCases) / lastMonthCases) * 100;
      } else if (thisMonthCases > 0) {
        monthlyGrowth = 100; // 100% growth if no cases last month but cases this month
      }

      setStats({
        totalFirms,
        activeProjects,
        openCases,
        monthlyGrowth: Math.round(monthlyGrowth),
      });
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const statsCards = [
    {
      title: 'Total Firms',
      value: statsLoading ? '...' : stats.totalFirms.toString(),
      icon: <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      color: '#1976d2',
    },
    {
      title: 'Active Projects',
      value: statsLoading ? '...' : stats.activeProjects.toString(),
      icon: <ProjectIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      color: '#dc004e',
    },
    {
      title: 'Open Cases',
      value: statsLoading ? '...' : stats.openCases.toString(),
      icon: <CaseIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      color: '#2e7d32',
    },
    {
      title: 'This Month',
      value: statsLoading ? '...' : `${stats.monthlyGrowth >= 0 ? '+' : ''}${stats.monthlyGrowth}%`,
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      color: '#ed6c02',
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: { xs: 2, sm: 4 }, display: { xs: 'none', sm: 'block' } }}>
        Welcome back, {user?.name}! Here's an overview of your PMP platform.
      </Typography>

      {/* Stats Cards - Only for PMP Admin */}
      {isPmpAdminOrSiteAdmin() && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
          {statsCards.map((stat, index) => (
            <Card key={index}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {stat.icon}
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="h4" component="div" sx={{ color: stat.color }}>
                      {statsLoading ? (
                        <CircularProgress size={24} sx={{ color: stat.color }} />
                      ) : (
                        stat.value
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* PMP Admin Quick Actions */}
      {isPmpAdminOrSiteAdmin() && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: { xs: 1.5, sm: 2 } }}>
            <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/pending-estimates')}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PendingIcon sx={{ fontSize: 30, color: 'warning.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h6">Pending Estimates</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Review vendor estimates
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            
            <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/vendor-invoices')}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InvoiceIcon sx={{ fontSize: 30, color: 'primary.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h6">Vendor Invoices</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage vendor billing
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            
            <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/firms')}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon sx={{ fontSize: 30, color: 'success.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h6">Manage Firms</Typography>
                    <Typography variant="body2" color="text.secondary">
                      View all firms
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Paper>
      )}

      {/* Main Content - Different layout for PMP Admin vs Other Users */}
      {isPmpAdminOrSiteAdmin() ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsIcon color="primary" />
                <Typography variant="h6">
                  Unread Notifications
                </Typography>
                {unreadCount > 0 && (
                  <Chip 
                    label={unreadCount} 
                    color="error" 
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
              {unreadCount > 0 && (
                <Button size="small" onClick={handleMarkAllAsRead}>
                  Mark all as read
                </Button>
              )}
            </Box>
            {loading ? (
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            ) : notifications.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No unread notifications.
              </Typography>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {notifications.map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItem disablePadding>
                      <ListItemButton 
                        onClick={() => handleNotificationClick(notification)}
                        sx={{ 
                          borderRadius: 1,
                          mb: 0.5,
                          bgcolor: notification.read_at ? 'transparent' : 'action.hover',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                          <CommentIcon 
                            sx={{ 
                              mr: 2, 
                              mt: 0.5,
                              color: notification.read_at ? 'text.secondary' : 'primary.main' 
                            }} 
                          />
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: notification.read_at ? 'normal' : 'bold',
                                    flex: 1 
                                  }}
                                >
                                  {notification.title}
                                </Typography>
                                {!notification.read_at && (
                                  <Chip 
                                    label="New" 
                                    color="primary" 
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                {notification.message && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {notification.message}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {new Date(notification.created_at).toLocaleString()}
                                </Typography>
                              </Box>
                            }
                          />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                    {index < notifications.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create new case, project, or manage firms.
            </Typography>
          </Paper>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          {/* Manage Firms Card for Non-Admin Users */}
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/firms')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">Manage Firms</Typography>
                  <Typography variant="body2" color="text.secondary">
                    View and manage your firms
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Unread Notifications for Non-PMP Admin Users */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsIcon color="primary" />
                <Typography variant="h6">
                  Unread Notifications
                </Typography>
                {unreadCount > 0 && (
                  <Chip 
                    label={unreadCount} 
                    color="error" 
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
              {unreadCount > 0 && (
                <Button size="small" onClick={handleMarkAllAsRead}>
                  Mark all as read
                </Button>
              )}
            </Box>
            {loading ? (
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            ) : notifications.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No unread notifications.
              </Typography>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {notifications.map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItem disablePadding>
                      <ListItemButton 
                        onClick={() => handleNotificationClick(notification)}
                        sx={{ 
                          borderRadius: 1,
                          mb: 0.5,
                          bgcolor: notification.read_at ? 'transparent' : 'action.hover',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                          <CommentIcon 
                            sx={{ 
                              mr: 2, 
                              mt: 0.5,
                              color: notification.read_at ? 'text.secondary' : 'primary.main' 
                            }} 
                          />
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: notification.read_at ? 'normal' : 'bold',
                                    flex: 1 
                                  }}
                                >
                                  {notification.title}
                                </Typography>
                                {!notification.read_at && (
                                  <Chip 
                                    label="New" 
                                    color="primary" 
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                {notification.message && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {notification.message}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {new Date(notification.created_at).toLocaleString()}
                                </Typography>
                              </Box>
                            }
                          />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                    {index < notifications.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard; 